import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Check, X, ChevronDown, Play } from "lucide-react";
import { openCookieSettings } from "@/lib/consent";
import {
    Accordion,
    AccordionContent,
    AccordionItem,
    AccordionTrigger,
} from "@/components/ui/accordion";
import ExitIntentModal from "@/components/ExitIntentModal";
import {
    IMAGES,
    WHATSAPP,
    WHATSAPP_CHANNEL,
    PRICE_ZONES,
    Z_PARAM_MAP,
    COUNTRY_ZONE,
    IDENTIFICATION,
    PROMISE,
    PILLARS,
    WEEKS,
    INCLUDES,
    COMPARISON_ROWS,
    NOT_FOR,
    FOR_YOU,
    FAQ,
} from "@/lib/landing-data";

/* Hook: reveal on scroll */
function useReveal() {
    useEffect(() => {
        const els = document.querySelectorAll(".reveal");
        const obs = new IntersectionObserver(
            (entries) =>
                entries.forEach((e) => {
                    if (e.isIntersecting) {
                        e.target.classList.add("is-visible");
                        obs.unobserve(e.target);
                    }
                }),
            { threshold: 0.05, rootMargin: "0px 0px -5% 0px" },
        );
        els.forEach((el) => obs.observe(el));
        // Safety net: after 1.5s, force-reveal anything still hidden
        const t = setTimeout(() => {
            document
                .querySelectorAll(".reveal:not(.is-visible)")
                .forEach((el) => {
                    const r = el.getBoundingClientRect();
                    if (r.top < window.innerHeight + 100) {
                        el.classList.add("is-visible");
                    }
                });
        }, 1500);
        return () => {
            obs.disconnect();
            clearTimeout(t);
        };
    }, []);
}

/* Hook: scroll parallax for photos & atmospheric layers */
function useParallax() {
    useEffect(() => {
        let raf = 0;
        const handler = () => {
            cancelAnimationFrame(raf);
            raf = requestAnimationFrame(() => {
                const els = document.querySelectorAll("[data-parallax]");
                els.forEach((el) => {
                    const speed = parseFloat(el.dataset.parallax || "0.15");
                    const rect = el.getBoundingClientRect();
                    const center =
                        rect.top + rect.height / 2 - window.innerHeight / 2;
                    el.style.transform = `translate3d(0, ${center * -speed}px, 0)`;
                });
            });
        };
        handler();
        window.addEventListener("scroll", handler, { passive: true });
        window.addEventListener("resize", handler);
        return () => {
            window.removeEventListener("scroll", handler);
            window.removeEventListener("resize", handler);
            cancelAnimationFrame(raf);
        };
    }, []);
}

/* Treated mentor photo: natural skin + cinematic color-grade + 3D float */
function MentorPhoto({ src, alt, testId, parallax, className = "" }) {
    return (
        <div
            data-parallax={parallax}
            className={`parallax-photo relative inline-block ${className}`}
        >
            <span aria-hidden className="photo-halo" />
            <img
                src={src}
                alt={alt}
                data-testid={testId}
                className="photo-treated relative w-full object-contain"
                loading="eager"
            />
            <div
                aria-hidden
                className="photo-overlay-cinema absolute inset-0 pointer-events-none"
            />
        </div>
    );
}

/* Atmospheric backdrop: orbs + grid/dot patterns layered behind a section */function Backdrop({ variant = "blue", pattern = "grid" }) {
    return (
        <div
            aria-hidden
            className="absolute inset-0 pointer-events-none overflow-hidden"
        >
            {pattern === "grid" && (
                <div className="absolute inset-0 bg-grid opacity-60" />
            )}
            {pattern === "dots" && (
                <div className="absolute inset-0 bg-dots opacity-50" />
            )}
            {pattern === "radial" && (
                <div className="absolute inset-0 bg-radial-fade" />
            )}
            {variant === "blue" && (
                <>
                    <div
                        className="orb orb-blue orb-drift-1"
                        style={{
                            width: 520,
                            height: 520,
                            top: "-10%",
                            left: "-8%",
                        }}
                    />
                    <div
                        className="orb orb-gold orb-drift-2"
                        style={{
                            width: 360,
                            height: 360,
                            bottom: "-8%",
                            right: "5%",
                        }}
                    />
                </>
            )}
            {variant === "gold" && (
                <>
                    <div
                        className="orb orb-gold orb-drift-3"
                        style={{
                            width: 480,
                            height: 480,
                            top: "20%",
                            right: "-12%",
                        }}
                    />
                    <div
                        className="orb orb-blue orb-drift-1"
                        style={{
                            width: 420,
                            height: 420,
                            bottom: "10%",
                            left: "-10%",
                        }}
                    />
                </>
            )}
            {variant === "violet" && (
                <>
                    <div
                        className="orb orb-violet orb-drift-2"
                        style={{
                            width: 560,
                            height: 560,
                            top: "-5%",
                            right: "-10%",
                        }}
                    />
                    <div
                        className="orb orb-blue orb-drift-3"
                        style={{
                            width: 340,
                            height: 340,
                            bottom: "15%",
                            left: "20%",
                        }}
                    />
                </>
            )}
            {variant === "dual" && (
                <>
                    <div
                        className="orb orb-blue orb-drift-1"
                        style={{
                            width: 480,
                            height: 480,
                            top: "10%",
                            left: "-5%",
                        }}
                    />
                    <div
                        className="orb orb-violet orb-drift-2"
                        style={{
                            width: 380,
                            height: 380,
                            top: "5%",
                            right: "10%",
                        }}
                    />
                    <div
                        className="orb orb-gold orb-drift-3"
                        style={{
                            width: 320,
                            height: 320,
                            bottom: "5%",
                            left: "40%",
                        }}
                    />
                </>
            )}
        </div>
    );
}

/* Tiny visual primitives */
const Eyebrow = ({ children, testId }) => (
    <p
        data-testid={testId}
        className="text-[11px] tracking-[0.35em] uppercase text-gold/80 font-medium"
    >
        {children}
    </p>
);

const SectionHeading = ({ children, className = "" }) => (
    <h2
        className={`font-serif font-light text-4xl sm:text-5xl md:text-6xl tracking-tight text-white leading-[1.05] ${className}`}
    >
        {children}
    </h2>
);

const PullQuote = ({ children, testId }) => (
    <div className="relative py-24 md:py-32 reveal fade-from-light" data-testid={testId}>
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <div className="blue-halo opacity-40" />
        </div>
        <div className="relative max-w-5xl mx-auto px-6 text-center">
            <span className="font-serif text-gold/60 text-7xl md:text-9xl leading-none">&ldquo;</span>
            <p className="pull-quote text-3xl sm:text-4xl md:text-5xl -mt-6">
                {children}
            </p>
            <div className="gold-divider mx-auto mt-10" />
        </div>
    </div>
);

/* WhatsApp brand glyph (lucide has no WhatsApp icon) */
function WhatsAppIcon({ className = "" }) {
    return (
        <svg
            viewBox="0 0 24 24"
            fill="currentColor"
            className={className}
            aria-hidden="true"
        >
            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51l-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
        </svg>
    );
}

export default function LandingPage() {
    useReveal();
    useParallax();
    const [exitOpen, setExitOpen] = useState(false);
    const exitFiredRef = useRef(false);

    /* ===== GEO PRICING ZONE ===== */
    const [zoneId, setZoneId] = useState(null); // null = still resolving
    const [countryName, setCountryName] = useState(null);

    useEffect(() => {
        if (typeof window === "undefined") return;
        const SS_ZONE = "dep_zone_v1";
        const SS_COUNTRY = "dep_country_v1";

        // 1) URL param ?z=... has top priority
        const params = new URLSearchParams(window.location.search);
        const z = params.get("z");
        if (z && Z_PARAM_MAP[z]) {
            const zid = Z_PARAM_MAP[z];
            const label = PRICE_ZONES[zid].label;
            sessionStorage.setItem(SS_ZONE, zid);
            sessionStorage.setItem(SS_COUNTRY, label);
            setZoneId(zid);
            setCountryName(label);
            return;
        }

        // 2) Previously resolved zone in this session
        const savedZone = sessionStorage.getItem(SS_ZONE);
        if (savedZone && PRICE_ZONES[savedZone]) {
            setZoneId(savedZone);
            setCountryName(sessionStorage.getItem(SS_COUNTRY) || null);
            return;
        }

        // 3) Detect country by IP (2s timeout → Zona 1)
        const controller = new AbortController();
        const timer = setTimeout(() => controller.abort(), 2000);
        fetch("https://ipapi.co/json/", { signal: controller.signal })
            .then((r) => r.json())
            .then((data) => {
                clearTimeout(timer);
                const code = String(data.country_code || data.country || "").toUpperCase();
                const entry = COUNTRY_ZONE[code];
                const zid = entry ? entry.zone : "z1";
                const name = entry ? entry.name : data.country_name || null;
                sessionStorage.setItem(SS_ZONE, zid);
                if (name) sessionStorage.setItem(SS_COUNTRY, name);
                setZoneId(zid);
                setCountryName(name);
            })
            .catch(() => {
                clearTimeout(timer);
                setZoneId("z1");
                setCountryName(null); // generic WhatsApp message
            });
        return () => clearTimeout(timer);
    }, []);

    const activeZone = zoneId ? PRICE_ZONES[zoneId] : PRICE_ZONES.z1;

    /* WhatsApp direct-chat URL with country-aware message */
    const waMessage = countryName
        ? `Hola Santi, vengo de la web de Desactiva el Patrón. Estoy en ${countryName} y quiero más información.`
        : "Hola Santi, vengo de la web de Desactiva el Patrón y quiero más información.";
    const waUrl = `https://wa.me/${WHATSAPP.number}?text=${encodeURIComponent(waMessage)}`;
    const openWhatsAppChat = () => {
        try {
            if (typeof window !== "undefined" && typeof window.fbq === "function") {
                window.fbq("track", "Contact", {
                    content_name: "WhatsApp direct chat",
                    content_category: "whatsapp_chat",
                });
            }
        } catch (e) {
            console.warn("Pixel track failed", e);
        }
        window.open(waUrl, "_blank", "noopener,noreferrer");
    };

    /* Hotmart checkout: fire Meta Pixel InitiateCheckout then open zone-aware URL (UTMs forwarded) */
    const goCheckout = (plan) => {
        const cfg = activeZone[plan]; // plan: "general" | "premium"
        if (!cfg) return;
        const contentName =
            plan === "general"
                ? "Desactiva el Patrón — General"
                : "Desactiva el Patrón — Premium";
        try {
            if (typeof window !== "undefined" && typeof window.fbq === "function") {
                window.fbq("track", "InitiateCheckout", {
                    content_ids: [`desactiva-patron-${plan}`],
                    content_name: contentName,
                    content_type: "product",
                    value: cfg.value,
                    currency: cfg.currency,
                });
            }
        } catch (e) {
            // pixel failures must never block checkout
            console.warn("Pixel track failed", e);
        }
        // Forward UTM params from the landing URL to Hotmart (keep ?off intact)
        let checkoutUrl = cfg.url;
        try {
            const target = new URL(cfg.url);
            const src = new URLSearchParams(window.location.search);
            ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term"].forEach(
                (k) => {
                    const v = src.get(k);
                    if (v) target.searchParams.set(k, v);
                },
            );
            checkoutUrl = target.toString();
        } catch (e) {
            console.warn("UTM forward failed", e);
        }
        window.open(checkoutUrl, "_blank", "noopener,noreferrer");
    };

    const buyGrupal = () => goCheckout("general");
    const buyPremium = () => goCheckout("premium");

    /* Scroll to pricing/enrollment section */
    const scrollToPricing = () => {
        const el = document.querySelector('[data-testid="pricing-section"]');
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    };

    /* WhatsApp broadcast channel: fire pixel + open channel (seats are closed; funnel goes here) */
    const openChannel = () => {
        try {
            if (typeof window !== "undefined" && typeof window.fbq === "function") {
                window.fbq("track", "Subscribe", {
                    content_name: WHATSAPP_CHANNEL.contentName,
                    content_category: "whatsapp_channel",
                });
                window.fbq("trackCustom", "SubscribeToChannel", {
                    channel: "whatsapp",
                });
            }
        } catch (e) {
            console.warn("Pixel track failed", e);
        }
        window.open(WHATSAPP_CHANNEL.url, "_blank", "noopener,noreferrer");
    };

    /* Exit-intent: mouse leaving viewport at top after a small delay */
    useEffect(() => {
        const KEY = "dep_exit_shown_v1";
        if (typeof window === "undefined") return;
        if (sessionStorage.getItem(KEY) === "1") return;
        let armed = false;
        const armTimer = setTimeout(() => {
            armed = true;
        }, 8000); // arm after 8s of dwell

        const onLeave = (e) => {
            if (!armed || exitFiredRef.current) return;
            if (e.clientY > 10) return; // only top exit
            exitFiredRef.current = true;
            sessionStorage.setItem(KEY, "1");
            setExitOpen(true);
        };
        document.addEventListener("mouseout", onLeave);
        return () => {
            clearTimeout(armTimer);
            document.removeEventListener("mouseout", onLeave);
        };
    }, []);

    return (
        <main className="relative bg-ink-900 text-white overflow-hidden">
            {/* ===== NAV ===== */}
            <Nav onEnroll={scrollToPricing} onChannel={openChannel} />

            {/* ===== HERO ===== */}
            <Hero onEnroll={scrollToPricing} />

            {/* ===== PAIN ===== */}
            <Pain />

            {/* ===== IDENTIFICATION ===== */}
            <Identification />

            {/* ===== PULL QUOTE ===== */}
            <PullQuote testId="quote-1">
                        No te falta fuerza. Te sobra un patrón que todavía obedeces.
            </PullQuote>

            {/* ===== REFRAMING ===== */}
            <Reframing />

            {/* ===== PROMISE ===== */}
            <Promise onCta={scrollToPricing} />

            {/* ===== HOW IT WORKS (LA EXPERIENCIA) ===== */}
            <HowItWorks />

            {/* ===== SIN MIEDO A LA HIPNOSIS ===== */}
            <Hypnosis />

            {/* ===== INCLUDES ===== */}
            <Includes onCta={scrollToPricing} zone={activeZone} />

            {/* ===== 8 WEEKS ===== */}
            <Weeks />

            {/* ===== PULL QUOTE ===== */}
            <PullQuote testId="quote-2">
                El viejo patrón no vuelve porque hayas fracasado. Vuelve para
                comprobar si esta vez vas a elegir diferente.
            </PullQuote>

            {/* ===== ABOUT SANTI ===== */}
            <About />

            {/* ===== SOCIAL PROOF ===== */}
            <SocialProof />

            {/* ===== PRICING ===== */}
            <Pricing
                zone={activeZone}
                loading={zoneId === null}
                onGrupal={buyGrupal}
                onPremium={buyPremium}
                onWhatsAppGeneral={openChannel}
            />

            {/* ===== COMPARISON ===== */}
            <Comparison zone={activeZone} />

            {/* ===== FOR WHOM ===== */}
            <ForWhom />

            {/* ===== FAQ ===== */}
            <FAQSection />

            {/* ===== FINAL CTA ===== */}
            <FinalCta zone={activeZone} onGrupal={buyGrupal} onPremium={buyPremium} onChannel={openChannel} />

            <Footer />

            {/* ===== FLOATING WHATSAPP ===== */}
            <button
                onClick={openWhatsAppChat}
                data-testid="whatsapp-float-button"
                aria-label="Háblame por WhatsApp"
                className="fixed bottom-5 right-5 z-50 flex items-center justify-center w-14 h-14 rounded-full transition-transform duration-300 hover:scale-110"
                style={{
                    backgroundColor: "#25D366",
                    boxShadow: "0 8px 24px rgba(37, 211, 102, 0.45)",
                }}
            >
                <WhatsAppIcon className="w-7 h-7 text-white" />
            </button>

            <ExitIntentModal
                open={exitOpen}
                onClose={() => setExitOpen(false)}
            />
        </main>
    );
}

/* =============== NAV =============== */
function Nav({ onEnroll, onChannel }) {
    return (
        <header
            className="fixed top-0 inset-x-0 z-40 backdrop-blur-xl border-b"
            style={{
                background: "rgba(14, 22, 38, 0.82)",
                borderColor: "rgba(244, 201, 181, 0.12)",
            }}
        >            <div className="max-w-7xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
                <a
                    href="#hero"
                    data-testid="nav-logo"
                    className="flex items-center gap-3"
                >
                    <img
                        src={IMAGES.sircsLogo}
                        alt="SIRCS · Santi Lorenzo"
                        data-testid="nav-logo-img"
                        className="h-12 md:h-14 w-auto"
                        style={{ mixBlendMode: "screen" }}
                    />
                </a>
                <nav className="hidden md:flex items-center gap-8 text-xs tracking-[0.2em] uppercase text-zinc-400">
                    <a href="#semanas" className="hover:text-gold transition-colors" data-testid="nav-metodo">Método</a>
                    <a href="#santi" className="hover:text-gold transition-colors" data-testid="nav-santi">Santi</a>
                    <a href="#precio" className="hover:text-gold transition-colors" data-testid="nav-precio">Inversión</a>
                </nav>
                <div className="flex items-center gap-2 sm:gap-3">
                    <button
                        onClick={onChannel}
                        data-testid="nav-channel-button"
                        className="btn-outline-gold !py-3 !px-4 text-[10px] hidden sm:inline-flex"
                    >
                        Canal WhatsApp
                    </button>
                    <button
                        onClick={onEnroll}
                        data-testid="nav-cta-button"
                        className="btn-gold !py-3 !px-5 text-[10px]"
                    >
                        Quiero inscribirme
                    </button>
                </div>
            </div>
        </header>
    );
}

/* =============== HERO =============== */
function Hero({ onEnroll }) {
    return (
        <section
            id="hero"
            data-testid="hero-section"
            className="section-dark relative min-h-screen pt-28 md:pt-36 pb-20 overflow-hidden"
        >
            <div className="lighthouse-beam" aria-hidden />
            <Backdrop variant="blue" pattern="grid" />
            <div className="blue-halo absolute -top-40 -left-40 opacity-90" />
            <div className="blue-halo absolute top-20 right-10 opacity-40" />

            <div className="max-w-3xl mx-auto px-6 lg:px-10 relative z-10">
                <div className="space-y-9">
                    <div className="space-y-4 reveal">
                        <Eyebrow testId="hero-eyebrow">
                            Mentoría grupal · 8 semanas en directo
                        </Eyebrow>
                        <h1
                            data-testid="hero-headline"
                            className="font-serif font-light text-5xl sm:text-6xl md:text-7xl lg:text-[5.5rem] leading-[0.95] tracking-tight"
                        >
                            DESACTIVA
                            <br />
                            EL <span className="text-gold italic">PATRÓN</span>
                        </h1>
                        <div className="gold-divider" />
                    </div>

                    <p className="text-lg sm:text-xl text-zinc-300 leading-relaxed font-light max-w-xl reveal">
                        En 8 semanas, deja de repetir la historia que llevas años
                        intentando cambiar.
                    </p>

                    <p className="text-sm text-zinc-400 leading-relaxed max-w-xl reveal">
                        Pon límites sin culpa. Decide sin miedo. Deja de sabotearte justo
                        cuando empieza a salir bien. Un proceso en grupo, en directo y
                        conmigo, para quien ya sabe lo que tiene que hacer… y necesita, por
                        fin, poder hacerlo.
                    </p>

                    <div
                        data-testid="enrollment-open-banner"
                        className="inline-flex items-center gap-3 px-4 py-2 border border-gold/40"
                        style={{ background: "rgba(244, 201, 181, 0.08)" }}
                    >
                        <span
                            className="w-2 h-2 rounded-full bg-gold animate-pulse"
                        />
                        <span className="text-[11px] tracking-[0.22em] uppercase text-gold font-medium">
                            Próxima promoción: enero 2027 · Plazas limitadas
                        </span>
                    </div>

                    <div className="space-y-4 reveal">
                        <button
                            onClick={onEnroll}
                            data-testid="hero-primary-cta"
                            className="btn-gold"
                        >
                            Quiero mi plaza
                        </button>
                        <p className="text-sm text-zinc-400">
                            ¿Dudas?{" "}
                            <a
                                href="https://calendly.com/santilorenzo/llamada-de-encaje-sesiones-privadas?utm_source=desactiva&utm_medium=hero"
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid="hero-call-link"
                                className="text-gold underline underline-offset-4 hover:text-beam transition-colors"
                            >
                                Reserva una llamada de 20 minutos conmigo
                            </a>
                        </p>
                    </div>
                </div>
            </div>

            <div className="absolute bottom-8 left-1/2 -translate-x-1/2 text-zinc-600 animate-pulse">
                <ChevronDown size={20} />
            </div>
        </section>
    );
}

/* =============== PAIN =============== */
function Pain() {
    const lines = [
        "Sabes que necesitas poner límites.",
        "Sabes que tienes que tomar decisiones.",
        "Sabes que deberías dejar de postergar.",
        "Sabes que podrías confiar más en ti.",
        "Sabes que hay conversaciones que llevas tiempo evitando.",
        "Sabes que tienes potencial.",
    ];
    const returns = [
        "Vuelve el miedo.",
        "Vuelve la excusa.",
        "Vuelve la culpa.",
        "Vuelve la duda.",
        "Vuelve el mismo patrón.",
    ];
    return (
        <section
            data-testid="pain-section"
            className="relative py-28 md:py-40 border-t border-white/5 overflow-hidden"
        >
            <Backdrop variant="violet" pattern="radial" />
            <div className="max-w-3xl mx-auto px-6 space-y-12 reveal relative">
                <SectionHeading className="text-center">
                    Sabes lo que tienes que hacer…
                    <br />
                    <span className="text-gold italic">pero vuelves a lo mismo.</span>
                </SectionHeading>

                <div className="space-y-2 text-lg sm:text-xl text-zinc-300 font-light leading-relaxed">
                    {lines.map((l) => (
                        <p key={l}>{l}</p>
                    ))}
                </div>

                <p className="text-zinc-400 italic font-serif text-xl">
                    Pero cuando llega el momento de actuar, algo dentro de ti vuelve a
                    tomar el control.
                </p>

                <div className="space-y-1 text-xl sm:text-2xl font-serif text-zinc-200">
                    {returns.map((r) => (
                        <p key={r}>{r}</p>
                    ))}
                </div>

                <div className="gold-divider" />

                <p className="text-base text-zinc-400 leading-relaxed">
                    Y lo más desgastante no es solo volver atrás. Lo más duro es empezar
                    a dejar de confiar en ti después de tantas veces prometiéndote que
                    esta vez sí ibas a cambiar.
                </p>

                <p className="text-xl sm:text-2xl text-white font-light">
                    Eso no es falta de capacidad.
                    <br />
                    Es un patrón automático que sigue dirigiendo tus decisiones.
                </p>
            </div>
        </section>
    );
}

/* =============== IDENTIFICATION =============== */
function Identification() {
    return (
        <section
            data-testid="identification-section"
            className="section-light fade-from-dark relative py-28 md:py-36"
        >
            <div className="max-w-6xl mx-auto px-6 space-y-16">
                <div className="max-w-3xl reveal space-y-4">
                    <Eyebrow>Esto puede estar pasándote</Eyebrow>
                    <SectionHeading>
                        Sin que te des cuenta,
                        <br />
                        <span className="text-gold italic">cada día.</span>
                    </SectionHeading>
                    <p className="text-zinc-400 text-lg max-w-xl">
                        Esta mentoría es para ti si te reconoces en cualquiera de estas señales:
                    </p>
                </div>

                <ul className="grid md:grid-cols-2 gap-x-12 gap-y-6">
                    {IDENTIFICATION.map((item, i) => (
                        <li
                            key={i}
                            data-testid={`identification-item-${i}`}
                            className="flex items-start gap-4 reveal"
                        >
                            <span className="font-serif text-gold/70 text-sm pt-1 min-w-[28px]">
                                {String(i + 1).padStart(2, "0")}
                            </span>
                            <p className="text-zinc-300 font-light leading-relaxed">
                                {item}
                            </p>
                        </li>
                    ))}
                </ul>

                <div className="max-w-3xl border-l-2 border-gold/40 pl-8 py-2 reveal">
                    <p className="font-serif text-2xl sm:text-3xl italic text-zinc-200 leading-snug">
                        Lo que hoy te frena, un día te protegió. Aprendiste a sobrevivir
                        así. Pero lo que te salvó entonces es justo lo que hoy no te deja
                        avanzar.
                    </p>
                </div>
            </div>
        </section>
    );
}

/* =============== REFRAMING =============== */
function Reframing() {
    const patterns = [
        "que evita",
        "que posterga",
        "que se adapta",
        "que busca aprobación",
        "que controla",
        "que se sabotea",
        "que vuelve atrás justo cuando algo empieza a cambiar",
    ];
    return (
        <section
            data-testid="reframing-section"
            className="section-dark relative py-28 md:py-36 border-t border-white/5"
        >
            <div className="max-w-4xl mx-auto px-6 space-y-12">
                <div className="reveal space-y-6">
                    <Eyebrow>El problema real</Eyebrow>
                    <SectionHeading>
                        No es que <span className="text-zinc-500 line-through">no sepas</span>.
                        <br />
                        <span className="text-gold italic">
                            Es que sigues funcionando desde el mismo programa.
                        </span>
                    </SectionHeading>
                </div>

                <p className="text-zinc-400 text-lg font-light leading-relaxed reveal">
                    La mayoría cree que necesita más motivación, más disciplina, más
                    fuerza de voluntad o más información. Pero el problema no es saber
                    más. Hay una parte de ti funcionando desde el mismo programa
                    automático.
                </p>

                <div className="reveal space-y-3 pl-6 border-l border-gold/20">
                    {patterns.map((p) => (
                        <p
                            key={p}
                            className="font-serif text-xl sm:text-2xl text-zinc-300 italic"
                        >
                            Un patrón <span className="text-gold">{p}</span>.
                        </p>
                    ))}
                </div>

                <div className="reveal pt-6">
                    <p className="text-xl sm:text-2xl font-light text-zinc-200 leading-relaxed">
                        Por eso puedes entenderlo todo… y aun así seguir repitiendo lo
                        mismo. Porque no basta con entender tu patrón.
                    </p>
                    <p className="font-serif text-3xl sm:text-4xl text-gold italic mt-4">
                        Hay que desactivarlo.
                    </p>
                </div>
            </div>
        </section>
    );
}

/* =============== PROMISE =============== */
function Promise({ onCta }) {
    const RESULTADOS = [
        "Dejas de reaccionar en automático y empiezas a elegir cómo respondes.",
        "Sabes exactamente qué patrón te hace repetir la misma historia… y cómo cortarlo.",
        "Pones límites sin pasarte días sintiéndote culpable.",
        "Tomas esas decisiones que llevas meses —o años— aplazando.",
        "Dejas de sabotearte justo cuando algo empieza a salir bien.",
        "Vuelves a confiar en tu palabra.",
        "Sales con un plan de 90 días para no volver al mismo lugar.",
    ];
    return (
        <section
            data-testid="promise-section"
            className="section-light fade-from-dark relative py-28 md:py-36"
        >
            <div className="max-w-6xl mx-auto px-6 grid lg:grid-cols-[1fr_1.2fr] gap-12 lg:gap-20 items-start">
                <div className="space-y-6 reveal lg:sticky lg:top-28">
                    <Eyebrow>En 8 semanas</Eyebrow>
                    <SectionHeading>
                        Así vas a vivir cuando el patrón{" "}
                        <span className="text-gold italic">deje de mandar.</span>
                    </SectionHeading>
                    <p className="text-zinc-400 text-base font-light leading-relaxed">
                        No te prometo una vida perfecta ni que no vuelvas a sentir miedo.
                        Te prometo algo más útil: que el miedo deje de decidir por ti.
                    </p>
                    <button
                        onClick={onCta}
                        data-testid="promise-cta-button"
                        className="btn-gold"
                    >
                        Quiero mi plaza
                    </button>
                </div>

                <ul className="space-y-5">
                    {RESULTADOS.map((p, i) => (
                        <li
                            key={i}
                            data-testid={`promise-item-${i}`}
                            className="flex items-start gap-5 reveal pb-5 border-b border-white/5"
                        >
                            <span className="font-serif text-gold text-3xl leading-none">
                                ✦
                            </span>
                            <p className="text-zinc-200 text-lg font-light leading-snug">
                                {p}
                            </p>
                        </li>
                    ))}
                </ul>
            </div>
        </section>
    );
}

/* =============== HOW IT WORKS =============== */
function HowItWorks() {
    return (
        <section
            data-testid="how-it-works-section"
            className="section-light relative py-28 md:py-36 border-t border-black/5 overflow-hidden"
        >
            <Backdrop variant="violet" pattern="dots" />
            <div className="max-w-7xl mx-auto px-6 space-y-16 relative">
                <div className="max-w-3xl reveal space-y-4">
                    <Eyebrow>La experiencia</Eyebrow>
                    <SectionHeading>
                        En directo, profunda
                        <br />y <span className="text-gold italic">acompañada</span>.
                    </SectionHeading>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                    {PILLARS.map((p, i) => (
                        <div
                            key={p.title}
                            data-testid={`pillar-${i}`}
                            className="glass-card p-8 lg:p-10 space-y-4 reveal"
                        >
                            <div className="flex items-center gap-4">
                                <span className="font-serif text-3xl text-gold">
                                    0{i + 1}
                                </span>
                                <h3 className="font-serif text-2xl sm:text-3xl text-white">
                                    {p.title}
                                </h3>
                            </div>
                            <p className="text-zinc-400 font-light leading-relaxed">
                                {p.desc}
                            </p>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
}

/* =============== INCLUDES =============== */
function Includes({ onCta, zone }) {
    return (
        <section
            data-testid="includes-section"
            className="section-dark-deep fade-from-light relative py-28 md:py-36"
        >
            <div className="max-w-5xl mx-auto px-6 space-y-12">
                <div className="reveal space-y-4">
                    <Eyebrow>Qué incluye</Eyebrow>
                    <SectionHeading className="max-w-3xl">
                        Todo lo que recibes al entrar en{" "}
                        <span className="text-gold italic">Desactiva el Patrón</span>.
                    </SectionHeading>
                </div>

                <ul className="grid md:grid-cols-2 gap-x-12 gap-y-4">
                    {INCLUDES.map((item, i) => {
                        const display =
                            zone && zone.id !== "z1" && item.includes("+74 € en General")
                                ? item.replace("+74 € en General", "opcionales en General")
                                : item;
                        return (
                            <li
                                key={i}
                                data-testid={`include-${i}`}
                                className="flex items-start gap-4 py-3 border-b border-white/5 reveal"
                            >
                                <Check size={18} className="text-gold mt-1 flex-shrink-0" />
                                <span className="text-zinc-200 font-light">{display}</span>
                            </li>
                        );
                    })}
                </ul>

                <div className="text-center pt-6 reveal">
                    <button
                        onClick={onCta}
                        data-testid="includes-cta-button"
                        className="btn-gold"
                    >
                        Quiero inscribirme
                    </button>
                </div>
            </div>
        </section>
    );
}

/* =============== 8 WEEKS =============== */
function Weeks() {
    const [open, setOpen] = useState(0);
    return (
        <section
            id="semanas"
            data-testid="weeks-section"
            className="section-light fade-from-dark relative py-28 md:py-36 overflow-hidden"
        >
            <Backdrop variant="blue" pattern="grid" />
            <div className="max-w-3xl mx-auto px-6 space-y-12 relative">
                <div className="reveal space-y-4 text-center">
                    <Eyebrow>Las 8 semanas</Eyebrow>
                    <SectionHeading>
                        Tu camino,{" "}
                        <span className="text-gold italic">semana a semana.</span>
                    </SectionHeading>
                </div>

                <div className="border-t border-black/10 reveal">
                    {WEEKS.map((w, i) => {
                        const isOpen = open === i;
                        return (
                            <div
                                key={w.n}
                                data-testid={`week-${i + 1}`}
                                className="border-b border-black/10"
                            >
                                <button
                                    type="button"
                                    onClick={() => setOpen(isOpen ? -1 : i)}
                                    data-testid={`week-toggle-${i + 1}`}
                                    aria-expanded={isOpen}
                                    className="w-full flex items-center justify-between gap-4 py-5 text-left group"
                                >
                                    <span className="flex flex-col sm:flex-row sm:items-baseline gap-1 sm:gap-4">
                                        <span className="font-serif text-xl sm:text-2xl text-zinc-200 leading-tight">
                                            {w.n} · {w.title}
                                        </span>
                                        <span className="text-[10px] tracking-[0.2em] uppercase text-zinc-500">
                                            {w.phase}
                                        </span>
                                    </span>
                                    <ChevronDown
                                        size={20}
                                        className={`flex-shrink-0 text-gold transition-transform duration-300 ${isOpen ? "rotate-180" : ""}`}
                                    />
                                </button>
                                {isOpen && (
                                    <div
                                        data-testid={`week-panel-${i + 1}`}
                                        className="pb-6 space-y-3 text-[13px]"
                                    >
                                        <p className="text-sm text-zinc-400 font-light leading-relaxed">
                                            {w.desc}
                                        </p>
                                        <Row label="Resultado" value={w.outcome} />
                                        <Row label="GPT SIRCS" value={w.gpt} />
                                        <Row label="Hipnomeditación" value={w.meditation} />
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            </div>
        </section>
    );
}

const Row = ({ label, value }) => (
    <div className="flex gap-3">
        <span className="text-zinc-500 min-w-[120px] tracking-wider uppercase text-[10px] pt-1">
            {label}
        </span>
        <span className="text-zinc-200 font-light">{value}</span>
    </div>
);

/* =============== HYPNOSIS (SIN MIEDO A LA HIPNOSIS) =============== */
function Hypnosis() {
    const MITOS = [
        {
            n: "1",
            mito: "«Voy a perder el control»",
            real: "Estás consciente todo el tiempo. Oyes todo, recuerdas todo y, si quieres abrir los ojos, los abres.",
        },
        {
            n: "2",
            mito: "«Me puedo quedar dentro»",
            real: "Nadie se queda dentro. Es un estado natural que ya vives cada día: cuando conduces y no recuerdas el trayecto, o cuando una película te atrapa por completo.",
        },
        {
            n: "3",
            mito: "«Conmigo no va a funcionar»",
            real: "No tienes que «dejarte». Te acompaño, no hago nada contra ti. Si puedes imaginar el olor del café recién hecho, puedes vivirlo.",
        },
        {
            n: "4",
            mito: "«Va contra mis creencias»",
            real: "No es una religión ni sustituye a la tuya. Es un trabajo de atención e imaginación. Mucha gente siente que en ese silencio conecta más con aquello que le trasciende. Llámalo como quieras.",
        },
    ];
    return (
        <section
            data-testid="hypnosis-section"
            className="section-light relative py-28 md:py-36 border-t border-black/5 overflow-hidden"
        >
            <Backdrop variant="violet" pattern="dots" />
            <div className="max-w-5xl mx-auto px-6 space-y-14 relative">
                <div className="max-w-3xl mx-auto text-center reveal space-y-4">
                    <Eyebrow>Las hipnomeditaciones</Eyebrow>
                    <SectionHeading>
                        La hipnosis no es lo que{" "}
                        <span className="text-gold italic">has visto en la tele.</span>
                    </SectionHeading>
                    <p className="text-zinc-400 font-light leading-relaxed">
                        Cada semana recibes una hipnomeditación: mi voz guiándote a un
                        estado de relajación profunda y atención total, para que lo que
                        trabajas en el Zoom llegue a donde la fuerza de voluntad no llega.
                        Estás consciente todo el tiempo y decides en todo momento.
                    </p>
                </div>

                <div className="grid sm:grid-cols-2 gap-6">
                    {MITOS.map((m, i) => (
                        <div
                            key={m.n}
                            data-testid={`hypnosis-card-${i + 1}`}
                            className="rounded-[20px] border border-black/10 bg-white/70 p-7 space-y-3 reveal"
                        >
                            <p className="text-sm text-zinc-500 line-through decoration-zinc-400/60">
                                {m.n} · {m.mito}
                            </p>
                            <p className="text-base text-zinc-200 font-light leading-relaxed">
                                {m.real}
                            </p>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
}

/* =============== ABOUT =============== */
function About() {
    return (
        <section
            id="santi"
            data-testid="about-section"
            className="section-dark relative py-28 md:py-36 border-t border-white/5 overflow-hidden"
        >
            <Backdrop variant="gold" pattern="radial" />
            <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-12 lg:gap-20 items-center relative">
                <div className="relative reveal">
                    <MentorPhoto
                        src={IMAGES.about}
                        alt="Santi Lorenzo"
                        testId="about-image"
                        parallax="0.1"
                        className="w-full max-w-[520px] mx-auto"
                    />
                </div>
                <div className="space-y-6 reveal">
                    <Eyebrow>Quién te acompaña</Eyebrow>
                    <SectionHeading>
                        Soy <span className="text-gold italic">Santi Lorenzo</span>.
                    </SectionHeading>
                    <p className="text-zinc-300 text-lg font-light leading-relaxed">
                        Experto en reprogramación mental y creador del sistema{" "}
                        <span className="text-gold">SIRCS</span>: Sistema Integral de
                        Reprogramación Consciente y Subconsciente.
                    </p>
                    <p className="text-zinc-400 leading-relaxed font-light">
                        Durante años he acompañado a personas que sienten que están
                        viviendo en piloto automático, repitiendo patrones, bloqueándose
                        ante decisiones importantes o saboteando su propio potencial.
                    </p>
                    <p className="text-zinc-400 leading-relaxed font-light">
                        Mi trabajo une mentalidad, inteligencia emocional, PNL,
                        hipnomeditación, visualización, trabajo de identidad y acción
                        consciente.
                    </p>
                    <div className="border-l-2 border-gold/50 pl-6 mt-6">
                        <p className="font-serif italic text-xl text-zinc-200 leading-snug">
                            No estoy aquí para darte más frases bonitas. Estoy aquí para
                            ayudarte a ver qué patrón está dirigiendo tu vida y acompañarte
                            a construir una nueva forma de responder.
                        </p>
                    </div>
                </div>
            </div>
        </section>
    );
}

/* =============== SOCIAL PROOF =============== */
/* Lightweight YouTube facade: loads the nocookie iframe only on click */
function VideoTestimonial({ id, index }) {
    const [playing, setPlaying] = useState(false);
    return (
        <div data-testid={`testimonial-${index}`} className="reveal">
            <div className="relative w-full aspect-[9/16] max-w-[320px] mx-auto overflow-hidden rounded-2xl border border-black/10 bg-black shadow-xl">
                {playing ? (
                    <iframe
                        data-testid={`testimonial-iframe-${index}`}
                        src={`https://www.youtube-nocookie.com/embed/${id}?autoplay=1&rel=0&playsinline=1`}
                        title="Testimonio en vídeo — Alumno/a de la Promoción 1"
                        className="absolute inset-0 w-full h-full"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                        allowFullScreen
                    />
                ) : (
                    <button
                        type="button"
                        onClick={() => setPlaying(true)}
                        data-testid={`testimonial-play-${index}`}
                        aria-label="Reproducir testimonio"
                        className="group absolute inset-0 w-full h-full cursor-pointer"
                    >
                        <img
                            src={`https://i.ytimg.com/vi/${id}/hqdefault.jpg`}
                            alt="Testimonio de alumno/a de la Promoción 1"
                            className="absolute inset-0 w-full h-full object-cover"
                            loading="lazy"
                        />
                        <span className="absolute inset-0 bg-black/25 transition-colors group-hover:bg-black/10" />
                        <span className="absolute left-1/2 top-1/2 flex h-16 w-16 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-gold text-ink-900 shadow-lg transition-transform duration-300 group-hover:scale-110">
                            <Play size={26} className="ml-1" fill="currentColor" />
                        </span>
                    </button>
                )}
            </div>
            <p className="text-center text-[11px] tracking-[0.25em] uppercase text-zinc-500 mt-4">
                Alumno/a de la Promoción 1
            </p>
        </div>
    );
}

function SocialProof() {
    return (
        <section
            data-testid="social-proof-section"
            className="section-light fade-from-dark relative py-28 md:py-36 overflow-hidden"
        >
            <Backdrop variant="violet" pattern="dots" />
            <div className="max-w-6xl mx-auto px-6 space-y-16 relative">
                <div className="max-w-3xl mx-auto text-center reveal space-y-4">
                    <Eyebrow>Testimonios</Eyebrow>
                    <SectionHeading>
                        Lo que cambió en quienes{" "}
                        <span className="text-gold italic">ya lo han vivido.</span>
                    </SectionHeading>
                </div>

                <div className="grid sm:grid-cols-2 gap-10 sm:gap-8 max-w-2xl mx-auto">
                    <VideoTestimonial id="9iJ0ISv74bk" index={0} />
                    <VideoTestimonial id="tVTY3JYEXVU" index={1} />
                </div>
            </div>
        </section>
    );
}

/* =============== PRICING =============== */
function Pricing({ zone, loading, onGrupal, onPremium, onWhatsAppGeneral }) {
    const gptsGeneral =
        zone.id === "z1"
            ? "GPTs SIRCS semanales — acceso opcional (+74 €)"
            : "GPTs SIRCS semanales — acceso opcional";
    return (
        <section
            id="precio"
            data-testid="pricing-section"
            className="section-light relative py-28 md:py-36 border-t border-black/5 overflow-hidden"
        >
            <Backdrop variant="dual" pattern="grid" />
            <div className="max-w-3xl mx-auto px-6 text-center relative reveal">
                <Eyebrow>La inversión</Eyebrow>
                <SectionHeading className="mt-4">
                    Elige cómo quieres{" "}
                    <span className="text-gold italic">vivir el proceso.</span>
                </SectionHeading>
                <p className="text-zinc-400 font-light leading-relaxed mt-6 max-w-2xl mx-auto">
                    <span className="text-gold">Inscripciones abiertas para la promoción que comienza en enero de 2027.</span>{" "}
                    Elige la opción que mejor encaje contigo.
                </p>
                <div
                    data-testid="payment-notice"
                    className="inline-flex items-start gap-3 mt-6 text-xs text-zinc-400 leading-relaxed text-left max-w-xl mx-auto"
                >
                    <span className="text-gold mt-0.5">◆</span>
                    <span>
                        <span className="text-zinc-200">¿Eliges la opción General?</span>{" "}
                        El acceso a los GPTs SIRCS es{" "}
                        {zone.id === "z1"
                            ? "un extra de 74 €"
                            : "un acceso opcional con coste extra"}{" "}
                        que se gestiona aparte tras la inscripción. En Premium van
                        incluidos sin coste adicional.
                    </span>
                </div>
            </div>

            <div className="max-w-5xl mx-auto px-6 mt-16 grid md:grid-cols-2 gap-8 relative">
                    {/* Grupal */}
                    <div
                        data-testid="pricing-card-grupal"
                        className="tilt-card glass-card p-8 lg:p-10 space-y-6"
                    >
                        <div>
                            <p className="text-[11px] tracking-[0.3em] uppercase text-zinc-500">
                                Opción 1
                            </p>
                            <h3 className="font-serif text-3xl text-white mt-2">
                                General
                            </h3>
                        </div>
                        <div className="relative">
                            {loading ? (
                                <div className="space-y-2 py-1" data-testid="grupal-price-loading">
                                    <div className="h-3 w-16 bg-white/10 rounded animate-pulse" />
                                    <div className="h-10 w-28 bg-white/10 rounded animate-pulse" />
                                </div>
                            ) : (
                                <>
                                    <p
                                        data-testid="grupal-price"
                                        className="font-serif text-5xl text-gold"
                                    >
                                        {zone.general.price}
                                    </p>
                                </>
                            )}
                            <p className="text-xs text-zinc-500 tracking-wider uppercase">
                                Comienza en enero 2027
                            </p>
                            <p
                                data-testid="grupal-installments"
                                className="text-[11px] text-gold/60 pt-1"
                            >
                                o hasta <span className="font-medium">3 cuotas</span>
                            </p>
                        </div>
                        <ul className="space-y-3 text-sm text-zinc-300 font-light pt-4 border-t border-white/5">
                            {[
                                "Acceso completo a las 8 semanas",
                                "Sesiones grupales por Zoom",
                                "Grupo privado de WhatsApp",
                                gptsGeneral,
                                "Hipnomeditaciones",
                                "Grabaciones de las sesiones",
                                "Plan SIRCS de 90 días",
                            ].map((f) => (
                                <li key={f} className="flex items-start gap-3">
                                    <Check
                                        size={16}
                                        className="text-gold mt-0.5 flex-shrink-0"
                                    />
                                    {f}
                                </li>
                            ))}
                        </ul>
                        <button
                            onClick={onGrupal}
                            disabled={loading}
                            data-testid="pricing-grupal-button"
                            className="btn-outline-gold w-full text-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            Quiero la versión general
                        </button>
                    </div>

                    {/* Premium */}
                    <div
                        data-testid="pricing-card-premium"
                        className="card-premium relative p-8 lg:p-10 space-y-6"
                    >
                        <div className="absolute -top-3 left-8 bg-gold text-ink-900 text-[10px] tracking-[0.25em] uppercase px-3 py-1">
                            Recomendada
                        </div>
                        <div>
                            <p className="text-[11px] tracking-[0.3em] uppercase text-gold/80">
                                Opción 2
                            </p>
                            <h3 className="font-serif text-3xl text-white mt-2">
                                Premium
                            </h3>
                        </div>
                        <div className="space-y-1">
                            {loading ? (
                                <div className="space-y-2 py-1" data-testid="premium-price-loading">
                                    <div className="h-3 w-16 bg-white/10 rounded animate-pulse" />
                                    <div className="h-10 w-28 bg-white/10 rounded animate-pulse" />
                                </div>
                            ) : (
                                <>
                                    <p
                                        data-testid="premium-price"
                                        className="font-serif text-5xl text-gold"
                                    >
                                        {zone.premium.price}
                                    </p>
                                </>
                            )}
                            <p className="text-xs text-zinc-500 tracking-wider uppercase">
                                Comienza en enero 2027
                            </p>
                            <p
                                data-testid="premium-installments"
                                className="text-[11px] text-gold/80 pt-1"
                            >
                                o hasta <span className="font-medium">4 cuotas</span>
                            </p>
                        </div>
                        <ul className="space-y-3 text-sm text-zinc-300 font-light pt-4 border-t border-white/5">
                            {[
                                "Todo lo de la versión general",
                                "Acceso a los GPTs SIRCS incluido (sin coste extra)",
                                "2 sesiones privadas 1:1 con Santi",
                                "Revisión personalizada de tu proceso",
                                "Prioridad en intervenciones en Zoom",
                                "Revisión de tus resultados de los GPTs",
                                "Adaptación de hipnomeditaciones",
                                "Revisión del Plan SIRCS de 90 días",
                            ].map((f) => (
                                <li key={f} className="flex items-start gap-3">
                                    <Check
                                        size={16}
                                        className="text-gold mt-0.5 flex-shrink-0"
                                    />
                                    {f}
                                </li>
                            ))}
                        </ul>
                        <button
                            onClick={onPremium}
                            disabled={loading}
                            data-testid="pricing-premium-button"
                            className="btn-gold w-full justify-center disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            Quiero la versión premium
                        </button>
                    </div>
                </div>
            <div className="max-w-5xl mx-auto px-6 mt-10 flex justify-center">
                <a
                    href="https://calendly.com/santilorenzo/llamada-de-encaje-sesiones-privadas?utm_source=desactiva&utm_medium=precios"
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="pricing-call-button"
                    className="btn-gold"
                >
                    Reserva una llamada de 20 minutos y te ayudo a elegir
                </a>
            </div>
            <div className="max-w-7xl mx-auto px-6 mt-10 text-center">
                <p
                    data-testid="whatsapp-general-hint"
                    className="text-sm text-zinc-400 font-light"
                >
                    ¿Tienes dudas antes de decidir? Escríbeme. Y si quieres,{" "}
                    <button
                        onClick={onWhatsAppGeneral}
                        data-testid="whatsapp-general-link"
                        className="text-gold underline underline-offset-4 hover:text-beam transition-colors"
                    >
                        únete al canal privado de WhatsApp
                    </button>{" "}
                    — gratuito, con reflexiones exclusivas, algún regalo y acceso a mi
                    próxima masterclass online gratuita.
                </p>
            </div>
        </section>
    );
}

/* =============== COMPARISON =============== */
function Comparison({ zone }) {
    return (
        <section
            data-testid="comparison-section"
            className="section-light relative py-28 md:py-36 border-t border-black/5"
        >
            <div className="max-w-5xl mx-auto px-6 space-y-12">
                <div className="reveal space-y-4">
                    <Eyebrow>Comparativa</Eyebrow>
                    <SectionHeading>
                        Diferencia entre
                        <br />
                        <span className="text-gold italic">General y Premium</span>.
                    </SectionHeading>
                </div>

                <div className="overflow-x-auto reveal">
                    <table className="w-full border-collapse" data-testid="comparison-table">
                        <thead>
                            <tr className="text-left">
                                <th className="py-4 pr-4 text-xs tracking-[0.2em] uppercase text-zinc-500 font-medium border-b border-white/10 w-[55%]">
                                    Incluye
                                </th>
                                <th className="py-4 px-4 text-center text-xs tracking-[0.2em] uppercase text-zinc-300 font-medium border-b border-white/10">
                                    General
                                </th>
                                <th className="py-4 px-4 text-center text-xs tracking-[0.2em] uppercase text-gold font-medium border-b border-gold/40">
                                    Premium
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {COMPARISON_ROWS.map(([feat, g, p], i) => {
                                const gVal =
                                    feat === "GPTs SIRCS" && zone && zone.id !== "z1"
                                        ? "acceso opcional con coste extra"
                                        : g;
                                return (
                                <tr key={feat} className="hover:bg-white/[0.02]">
                                    <td className="py-4 pr-4 text-zinc-200 text-sm font-light border-b border-white/5">
                                        {feat}
                                    </td>
                                    <td className="py-4 px-4 text-center border-b border-white/5">
                                        {typeof gVal === "string" ? (
                                            <span className="text-[11px] leading-snug text-zinc-300 font-light">{gVal}</span>
                                        ) : gVal ? (
                                            <Check size={18} className="inline text-gold" />
                                        ) : (
                                            <X size={18} className="inline text-zinc-700" />
                                        )}
                                    </td>
                                    <td className="py-4 px-4 text-center border-b border-white/5">
                                        {typeof p === "string" ? (
                                            <span className="text-[11px] leading-snug text-gold font-light">{p}</span>
                                        ) : p ? (
                                            <Check size={18} className="inline text-gold" />
                                        ) : (
                                            <X size={18} className="inline text-zinc-700" />
                                        )}
                                    </td>
                                </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>
        </section>
    );
}

/* =============== FOR WHOM =============== */
function ForWhom() {
    return (
        <section
            data-testid="for-whom-section"
            className="section-dark fade-from-light relative py-28 md:py-36"
        >
            <div className="max-w-6xl mx-auto px-6 space-y-12">
                <div className="reveal text-center space-y-4 max-w-3xl mx-auto">
                    <Eyebrow>Para ti — o no</Eyebrow>
                    <SectionHeading>
                        Esto <span className="text-zinc-500">no</span> es{" "}
                        <span className="text-gold italic">para todo el mundo</span>.
                    </SectionHeading>
                </div>

                <div className="grid md:grid-cols-2 gap-px bg-white/5">
                    <div
                        data-testid="not-for-card"
                        className="bg-ink-900 p-10 space-y-5"
                    >
                        <h3 className="text-xs tracking-[0.3em] uppercase text-zinc-500">
                            No es para ti si…
                        </h3>
                        <ul className="space-y-4">
                            {NOT_FOR.map((n) => (
                                <li
                                    key={n}
                                    className="flex items-start gap-3 text-zinc-400 font-light"
                                >
                                    <X
                                        size={16}
                                        className="text-zinc-600 mt-1 flex-shrink-0"
                                    />
                                    {n}
                                </li>
                            ))}
                        </ul>
                    </div>
                    <div
                        data-testid="for-you-card"
                        className="bg-ink-900 p-10 space-y-5 border border-gold/30"
                    >
                        <h3 className="text-xs tracking-[0.3em] uppercase text-gold">
                            Sí es para ti si…
                        </h3>
                        <ul className="space-y-4">
                            {FOR_YOU.map((n) => (
                                <li
                                    key={n}
                                    className="flex items-start gap-3 text-zinc-200 font-light"
                                >
                                    <Check
                                        size={16}
                                        className="text-gold mt-1 flex-shrink-0"
                                    />
                                    {n}
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
            </div>
        </section>
    );
}

/* =============== FAQ =============== */
function FAQSection() {
    return (
        <section
            id="faq"
            data-testid="faq-section"
            className="section-light fade-from-dark relative py-28 md:py-36"
        >
            <div className="max-w-3xl mx-auto px-6 space-y-12">
                <div className="reveal space-y-4 text-center">
                    <Eyebrow>Preguntas frecuentes</Eyebrow>
                    <SectionHeading>
                        Lo que <span className="text-gold italic">necesitas saber</span>.
                    </SectionHeading>
                </div>

                <Accordion type="single" collapsible className="w-full reveal">
                    {FAQ.map((f, i) => (
                        <AccordionItem
                            key={i}
                            value={`item-${i}`}
                            data-testid={`faq-item-${i}`}
                            className="border-b border-white/10"
                        >
                            <AccordionTrigger
                                data-testid={`faq-trigger-${i}`}
                                className="text-left font-serif text-xl text-white hover:text-gold hover:no-underline py-6"
                            >
                                {f.q}
                            </AccordionTrigger>
                            <AccordionContent className="text-zinc-400 font-light leading-relaxed pb-6 pr-6">
                                {f.a}
                            </AccordionContent>
                        </AccordionItem>
                    ))}
                </Accordion>
            </div>
        </section>
    );
}

/* =============== FINAL CTA =============== */
function FinalCta({ zone, onGrupal, onPremium, onChannel }) {
    return (
        <section
            data-testid="final-cta-section"
            className="section-dark fade-from-light relative py-28 md:py-40 overflow-hidden"
        >
            <Backdrop variant="gold" pattern="grid" />
            <div className="blue-halo absolute -top-40 left-1/3 opacity-50" />
            <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-16 items-center relative">
                <div className="space-y-8 reveal">
                    <Eyebrow>Es el momento</Eyebrow>
                    <SectionHeading>
                        Puedes seguir esperando
                        <br />
                        el <span className="text-gold italic line-through">momento perfecto</span>.
                    </SectionHeading>
                    <p className="text-zinc-400 leading-relaxed font-light">
                        Y eso también es el patrón. Esperar sentirte
                        preparado antes de cambiar. Esperar que el miedo desaparezca antes
                        de actuar.
                    </p>
                    <p className="font-serif italic text-2xl text-zinc-200">
                        Y mientras esperas… tu vida sigue pasando.
                    </p>
                    <div className="gold-divider" />
                    <p className="font-serif text-3xl sm:text-4xl text-white leading-tight">
                        Ha llegado el momento de{" "}
                        <span className="text-gold italic">desactivarlo</span>.
                    </p>

                    <div className="space-y-3 pt-4">
                        <p className="text-[11px] tracking-[0.3em] uppercase text-gold/80">
                            Inscripciones abiertas
                        </p>
                        <p className="font-serif text-2xl text-white">
                            Elige tu plaza hoy y{" "}
                            <span className="text-gold italic">empieza el proceso</span>.
                        </p>
                    </div>

                    <div className="flex flex-col sm:flex-row gap-4 pt-4">
                        <button
                            onClick={onGrupal}
                            data-testid="final-grupal-cta"
                            className="btn-outline-gold justify-center"
                        >
                            Versión general · {zone.general.price}
                        </button>
                        <button
                            onClick={onPremium}
                            data-testid="final-premium-cta"
                            className="btn-gold justify-center"
                        >
                            Versión premium · {zone.premium.price}
                        </button>
                    </div>

                    <button
                        onClick={onChannel}
                        data-testid="final-channel-cta"
                        className="text-sm text-gold underline underline-offset-4 hover:text-beam transition-colors"
                    >
                        ¿Dudas antes de decidir? Únete al canal de WhatsApp
                    </button>

                    <p className="text-xs text-zinc-500 leading-relaxed">
                        Inscripciones abiertas para la promoción que comienza en
                        enero de 2027. Elige la opción que mejor encaje contigo y
                        empieza cuando arranque la edición.
                    </p>
                </div>

                <div className="relative reveal">
                    <div className="absolute -inset-12 blue-halo opacity-60" />
                    <img
                        src={IMAGES.finalCta}
                        alt="Santi Lorenzo"
                        data-testid="final-image"
                        data-parallax="0.1"
                        className="parallax-photo relative w-full max-w-[480px] mx-auto"
                    />
                </div>
            </div>
        </section>
    );
}

/* =============== FOOTER =============== */
function Footer() {
    return (
        <footer
            data-testid="footer"
            className="border-t border-white/5 py-12 px-6"
        >
            <div className="max-w-7xl mx-auto space-y-6">
                <div className="flex flex-col md:flex-row items-center justify-between gap-6">
                    <p className="font-serif text-lg">
                        Santi <span className="text-gold">Lorenzo</span> · Mentalidad SIRCS
                    </p>
                    <nav className="flex flex-wrap items-center justify-center gap-x-5 gap-y-2 text-xs text-zinc-400">
                        <Link
                            to="/aviso-legal"
                            data-testid="footer-aviso-legal"
                            className="hover:text-gold transition-colors"
                        >
                            Aviso legal
                        </Link>
                        <Link
                            to="/politica-de-privacidad"
                            data-testid="footer-privacidad"
                            className="hover:text-gold transition-colors"
                        >
                            Política de privacidad
                        </Link>
                        <Link
                            to="/politica-de-cookies"
                            data-testid="footer-cookies"
                            className="hover:text-gold transition-colors"
                        >
                            Política de cookies
                        </Link>
                        <button
                            onClick={openCookieSettings}
                            data-testid="footer-configure-cookies"
                            className="hover:text-gold transition-colors"
                        >
                            Configurar cookies
                        </button>
                    </nav>
                    <p className="text-xs text-zinc-500 tracking-wider">
                        © {new Date().getFullYear()} — Desactiva el Patrón. Todos los
                        derechos reservados.
                    </p>
                </div>
                <p
                    data-testid="footer-contact"
                    className="text-center text-xs text-zinc-400"
                >
                    Contacto:{" "}
                    <a
                        href="mailto:santilorenzo@desactivaelpatron.com"
                        data-testid="footer-email"
                        className="text-gold hover:text-beam transition-colors"
                    >
                        santilorenzo@desactivaelpatron.com
                    </a>
                    {" · "}WhatsApp{" "}
                    <a
                        href={`https://wa.me/${WHATSAPP.number}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="footer-whatsapp"
                        className="text-gold hover:text-beam transition-colors"
                    >
                        +34 605 045 728
                    </a>
                </p>
            </div>
        </footer>
    );
}
